<template>
  <v-card color="transparent" elevation="0">
    <v-card-title v-if="!hideTitle" class="d-flex align-center">{{ title }}
      <v-btn
        v-if="showResetFilters"
        :disabled="!Object.keys(filters)?.length && withintable === '' && !sortBy?.length"
        elevation="1"
        @click="resetFiltersAndSorting()"
        size="small"
        class="ml-2"
        title="Reset Filters & Sorting"
        icon="mdi-filter-remove"
        tile
        variant="text"
      ></v-btn>

      <div>
        <v-btn
          v-if="showUnselectAll"
          id="unselect-all-btn"
          :disabled="!selected?.length"
          @click="selected = [];"
          class="ml-2"
          size="small"
          variant="text"
          tile
          title="Unselect All"
          icon="mdi-close-circle-outline"
          ></v-btn>
          <span
            v-show="currentProductMismatchesSks.length && title==='Mismatches' && $route.path==='/product-matching'"
            class="text-orange-lighten-1 pl-6 text-subtitle-1"
            v-text="`${currentProductMismatchesSks.length} selected`"
          ></span>
          <span
            v-if="showCrudActions && selected?.length"
            class="text-orange-lighten-1 pl-4 text-subtitle-1"
            v-text="`${selected.length} selected`"
          ></span>
      </div>

      <slot name="topFilter"></slot>

      <v-spacer v-if="showCrudActions"></v-spacer>

      <!-- CRUD Action Buttons -->
      <div v-if="showCrudActions" class="d-flex gap-2 flex-shrink-0 mr-4">
        <v-btn
          v-if="showMergeButton"
          color="warning"
          variant="tonal"
          size="default"
          :disabled="!selected?.length || selected.length < 2"
          @click="$emit('merge', selectedRecords)"
        >
          <v-icon start>mdi-call-merge</v-icon>
          Merge {{ selected?.length > 0 ? `(${selected.length})` : '' }}
        </v-btn>

        <v-btn
          v-if="showAddButton"
          color="primary"
          variant="tonal"
          size="default"
          @click="$emit('add')"
        >
          <v-icon start>mdi-plus</v-icon>
          Add
        </v-btn>
      </div>

      <v-text-field
        ref="search"
        density="compact"
        class="mx-5 mb-4"
        style="min-width: 200px"
        name="search"
        :model-value="withintable"
        @update:model-value="debounceSearch($event, 'global')"
        placeholder="Search"
        variant="underlined"
        single-line
        hide-details
        clearable
        ></v-text-field>
      </v-card-title>
    <v-data-table
      @click:row="handleRowSelection"
      :show-select="showSelect"
      :select-strategy="selectStrategy"
      v-model="selected"
      :show-expand="showExpand"
      single-expand
      density="compact"
      :headers="headers"
      :items="filteredItems"
      :item-value="itemKey"
      v-model:items-per-page="itemsPerPage"
      :header-props="headerProps"
      :cell-props="cellProps"
      :items-per-page-options="footerProps?.itemsPerPageOptions || [ 5, 10, 15, 20, 25, 50 ]"
      :show-current-page="footerProps?.showCurrentPage"
      multi-sort
      :sort-by="sortBy"
      v-model:page="page"
      :loading="loading"
      :class="['elevation-1', tableContainerClass]"
      :search="withintable"
      :custom-filter="customFilter"
      @update:expanded="$emit('expanded', $event)"
      :expanded="expanded"
      :fixed-header="fixedHeader"
      :height="height ? height : defaultHeight"
      :ref="title + 'dataTable'"
      :group-by="groupBy ? groupBy : undefined"
      :row-props="rowProps"
      @update:modelValue="handleModel"
      mobile-breakpoint="0"
    >
      <template v-slot:item.data-table-select="data">
        <v-checkbox-btn
          @click.stop="handleRowSelection($event, data)"
          :model-value="data.isSelected(data.internalItem)"
        ></v-checkbox-btn>
      </template>
      <template v-slot:thead>
        <slot name="thead"></slot>
      </template>
      <template v-slot:[`header.${productAttributesField}`]>
        Attributes
        <div class="d-flex">
        <v-btn
          title="Small"
          :class="attributesWidth==200 ? 'bg-primary' : null"
          size="x-small"
          density="compact"
          variant="text"
          icon="mdi-size-s"
          @click.stop="attributesWidth=200"
        ></v-btn>
        <v-btn
          title="Medium"
          :class="attributesWidth==420 ? 'bg-primary' : null"
          size="x-small"
          density="compact"
          variant="text"
          icon="mdi-size-m"
          @click.stop="attributesWidth=420"
        ></v-btn>
        <v-btn
          label
          title="Large"
          :class="attributesWidth==600 ? 'bg-primary' : null"
          size="x-small"
          density="compact"
          variant="text"
          icon="mdi-size-l"
          @click.stop="attributesWidth=600"
        ></v-btn>
        <v-btn
          variant="text"
          density="compact"
          title="Auto"
          :class="attributesWidth==undefined ? 'bg-primary' : null"
          size="x-small"
          @click.stop="attributesWidth=undefined"
        >
          AUTO
        </v-btn>
        </div>
      </template>
      <template v-slot:header.GR_Fields>
        GR Fields
        <div class="d-flex">
        <v-btn
          title="Small"
          :class="grFieldsWidth==200 ? 'bg-primary' : null"
          size="x-small"
          density="compact"
          variant="text"
          icon="mdi-size-s"
          @click.stop="grFieldsWidth=200"
        ></v-btn>
        <v-btn
          title="Medium"
          :class="grFieldsWidth==420 ? 'bg-primary' : null"
          size="x-small"
          density="compact"
          variant="text"
          icon="mdi-size-m"
          @click.stop="grFieldsWidth=420"
        ></v-btn>
        <v-btn
          label
          title="Large"
          :class="grFieldsWidth==600 ? 'bg-primary' : null"
          size="x-small"
          density="compact"
          variant="text"
          icon="mdi-size-l"
          @click.stop="grFieldsWidth=600"
        ></v-btn>
        <v-btn
          variant="text"
          density="compact"
          title="Auto"
          :class="grFieldsWidth==undefined ? 'bg-primary' : null"
          size="x-small"
          @click.stop="grFieldsWidth=undefined"
        >
          AUTO
        </v-btn>
        </div>
      </template>
      <template v-slot:[`item.${itemKey}`]="{ item }">
        <span
          :id="`${itemKey}:${item[itemKey]}`"
          v-text="item[itemKey]"
          class="text-no-wrap"
        >
        </span>
      </template>

       <template v-slot:item.Valid_From_Date="{ item }">
        <span :class="item.Valid_From_Date === $minDate ? 'text-grey' : ''">
          {{ moment(item.Valid_From_Date).format('DD/MM/YYYY') }}
        </span>
      </template>

      <template v-slot:item.Valid_To_Date="{ item }">
        <span :class="item.Valid_To_Date === $maxDate ? 'text-grey' : ''">
          {{ moment(item.Valid_To_Date).format('DD/MM/YYYY') }}
        </span>
      </template>

      <template v-slot:[`item.${productAttributesField}`]="{ item }">
        <div
          v-if="item[productAttributesField]"
          :style="attributesWidth ? `overflow: auto; text-overflow: ellipsis; white-space: nowrap; max-width: ${attributesWidth}px;` : ''"
          class="d-block scrollbar"
        >
          <div style="font-size: 13px; line-height: 0.91rem;" class="d-block" v-for="(value, name, index) in JSON.parse(
            item[productAttributesField],
            (k, v) => (k.toLowerCase() === 'cookie') ? undefined : v)" :key="name"
          >
            <span class="text-grey" v-html="name.replace(' ', '&nbsp') + ':&nbsp;'"></span><span v-text="value"/>
            <br v-if="index+1 < getProductAttrsLength(item)"/>
          </div>
        </div>
      </template>

      <template v-slot:item.Matching_TS="{ item }">
        <MatchingTS :item="item"/>
      </template>

      <template v-slot:item.useProductAsTemplate="{ item }">
        <div class="justify-center">
          <v-btn
            icon
            size="x-small"
            class="d-inline-block"
            @click.stop="$emit('useAsTemplateProductSk', item.Product_SK)"
            title="Use as Template"
            >
            <v-icon>
              mdi-content-copy
            </v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:item.createFromSuggestions="{ item }">
        <div class="justify-center">
          <v-btn
            icon
            size="x-small"
            class="d-inline-block"
            @click.stop="$emit('createFromSuggestionsProductMatchingSk', item.Product_Matching_SK)"
            
            title="Create from suggestions"

            >
            <v-icon>
              mdi-magic-staff
            </v-icon>
          </v-btn>
        </div>


      </template>

      <template v-slot:item.PM_Decision_TS="{ item }">
        <MatchingTS
          :item="{
            Matching_TS: item.PM_Decision_TS,
            Matching_Origin_Process: item.PM_Decision_Process,
            Matching_Origin_Operator: item.PM_Decision_Operator,
            Matching_Status: item.PM_Decision
          }"
        />
      </template>

      <template v-slot:item.Matching_Status="{ item }">
        <v-icon v-if="item.Matching_Status==='Matched'" size="small" color="green" title="Matched">mdi-check-all</v-icon>
        <v-icon v-else-if="item.Matching_Status==='Discarded'" size="small" title="Discarded" color="red-lighten-1">mdi-delete</v-icon>
        <v-icon v-else-if="item.Matching_Status==='Not Matched'" size="small" color="yellow" title="Not Matched">mdi-code-tags</v-icon>
        <span class="text-no-wrap ml-1">{{ item.Matching_Status }}</span>
      </template>

      <template v-slot:item.TREX="{ item }">
        <v-chip
          v-if="item.TREX === true || item.TREX === 'TRUE'"
          size="x-small"
          color="orange-darken-2"
          variant="flat"
          class="font-weight-bold"
        >
          TRUE
        </v-chip>
        <span v-else>{{ item.TREX }}</span>
      </template>

      <template v-slot:item.Mutation_Logs_Count="{ item }">
        <MutationLogsButton
          :recordId="item[itemKey]"
          :itemKey="itemKey"
          :databaseLogCount="item.Mutation_Logs_Count"
        />
      </template>

      <template v-slot:item.Payload_JSON="{ item }">
        <div v-if="item.Payload_JSON" class="payload-json-container">
          <vue-json-pretty
            :data="parsePayload(item.Payload_JSON)"
            :deep="0"
            showLength
            :showIcon="false"
            :showLineNumber="false"
          />
        </div>
      </template>

      <template v-slot:item.Total_Amount_USD="{ item }">
        <span
          v-if="item.Total_Amount_USD != null"
          :title="formatAsCurrency(item.Total_Amount_USD, 'USD')"
        >
          {{ formatCompactCurrency(item.Total_Amount_USD, 'USD') }}
        </span>
      </template>

      <!-- CRUD Actions Column -->
      <template v-if="showCrudActions" v-slot:item.actions="{ item }">
        <div class="justify-center" @click.stop>
          <v-btn
            icon
            size="x-small"
            class="d-inline-block"
            @click="$emit('edit', item)"
            :title="`Edit ${entityName || 'Item'}`"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            size="x-small"
            class="d-inline-block"
            @click="$emit('delete', item)"
            :title="`Delete ${entityName || 'Item'}`"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- Custom Actions Column (passthrough slot) -->
      <template v-else-if="$slots['item.actions']" v-slot:item.actions="{ item }">
        <slot name="item.actions" :item="item"></slot>
      </template>

      <template v-slot:item.GR_Fields="{ item }">
        <div
          class="d-block scrollbar"
          :style="grFieldsWidth ? `overflow: auto; text-overflow: ellipsis; white-space: nowrap; max-width: ${grFieldsWidth}px;` : ''"
        >
          <div v-if="item.GR_Brand_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Brand:&nbsp;</span><span>{{ item.GR_Brand_Name }}</span>
          </div>
          <div v-if="item.GR_Categ_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Category:&nbsp;</span><span>{{ item.GR_Categ_Name }}</span>
          </div>
          <div v-if="item.GR_Line_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Line:&nbsp;</span><span>{{ item.GR_Line_Name }}</span>
          </div>
          <div v-if="item.GR_Product_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Product:&nbsp;</span><span>{{ item.GR_Product_Name }}</span>
          </div>
          <div v-if="item.GR_Multi_Pack" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Pack:&nbsp;</span><span>{{ item.GR_Multi_Pack }}</span>
          </div>
          <div v-if="item.GR_TREX" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">TREX:&nbsp;</span><span>{{ item.GR_TREX }}</span>
          </div>
        </div>
      </template>

      <template v-slot:item.Suggestions="{ item }">
        <div v-if="hasSuggestions(item)" class="d-block scrollbar" style="overflow: auto; text-overflow: ellipsis; white-space: nowrap; max-width: 420px;">
          <div v-if="item.Suggested_Brand_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Brand:&nbsp;</span><span>{{ item.Suggested_Brand_Name }}</span>
          </div>
          <div v-if="item.Suggested_Line_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Line:&nbsp;</span><span>{{ item.Suggested_Line_Name }}</span>
          </div>
          <div v-if="item.Suggested_Product_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Product:&nbsp;</span><span>{{ item.Suggested_Product_Name }}</span>
          </div>
          <div v-if="item.Suggested_Category_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Category:&nbsp;</span><span>{{ item.Suggested_Category_Name }}</span>
          </div>
          <div v-if="item.Suggested_Multi_Pack_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Multi Pack:&nbsp;</span><span>{{ item.Suggested_Multi_Pack_Name }}</span>
          </div>
          <div v-if="item.Suggested_Benefit_Name" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Benefit:&nbsp;</span><span>{{ item.Suggested_Benefit_Name }}</span>
          </div>
          <div v-if="item.Suggested_Discard !== null" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">Discard:&nbsp;</span><span>{{ item.Suggested_Discard }}</span>
          </div>
          <div v-if="item.Suggested_TREX" style="font-size: 13px; line-height: 0.91rem;" class="d-block">
            <span class="text-grey">TREX:&nbsp;</span><span>{{ item.Suggested_TREX }}</span>
          </div>
        </div>
      </template>

      <!-- useful for every table -->
      <template v-slot:body.prepend="{ columns }">
        <tr
          v-if="columns?.some(h => h.filterType)"
        >
          <td v-for="(col, index) in columns" :key="index" class="px-1">
            <v-autocomplete
              :items="uniqueItems(col.key)"
              v-model="filters[col.key]"
              v-if="col.filterType === 'combo'"
              name="col.text"
              multiple
              density="compact"
              single-line
              variant="underlined"
              size="1"
              hide-details
              :menu-props="{ maxHeight: 600 }"
              clearable
              autocomplete="off"
            ></v-autocomplete>
            <v-select
              :items="uniqueItems(col.key)"
              v-model="filters[col.key]"
              v-else-if="col.filterType === 'list'"
              name="col.text"
              multiple
              size="1"
              density="compact"
              variant="underlined"
              single-line
              hide-details
              :menu-props="{ maxHeight: 600 }"
              clearable
              style="font-size: 14px"
              autocomplete="off"
            ></v-select>
            <v-text-field
              clearable
              size="1"
              v-else-if="col.filterType === 'text'"
              @update:model-value="debounceSearch($event, 'filters', col.key)"
              :model-value="filters[col.key]"
              name="col.text"
              hide-details
              variant="underlined"
              single-line
              density="compact"
              style="font-size: 14px"
              autocomplete="off"
            >
            </v-text-field>
            <v-text-field
              clearable
              size="1"
              v-else-if="col.filterType === 'number'"
              @update:model-value="debounceSearch($event, 'filters', col.key)"
              :model-value="filters[col.key]"
              name="col.text"
              hide-details
              hide-spin-buttons
              type="number"
              variant="underlined"
              single-line
              density="compact"
              style="font-size: 14px; min-width: 80px;"
              autocomplete="off"
            >
            </v-text-field>
            <slot :name="'headerFilter.' + col.key">
            </slot>
          </td>
        </tr>
      </template>

      <template v-slot:expanded-row="{ item, columns, index }">
        <tr>
          <td :colspan="columns.length" class="pa-0">
            <slot name="expanded-content" :item="item" :columns="columns" :index="index"></slot>
          </td>
        </tr>
      </template>

      <template v-if="hideFooter" #bottom></template>

<!--       <template
        v-if="$route.path === '/product-matching'"
        v-slot:body.append="{ selectAll, allSelected }"
      >
        <v-btn
          v-if="selectStrategy === 'page' && selectionStateName && filteredItems?.length > 0"
          id="select-visible-items-btn"
          @click="selectAll(true)"
          :disabled="allSelected"
          variant="text"
          size="small"
          prepend-icon="mdi-checkbox-multiple-outline"
        >
          Select visible items
        </v-btn>
      </template> -->
    </v-data-table>
  </v-card>
</template>

<script>
import { inject } from 'vue'
import { mapState, mapMutations } from 'vuex'
// import Question from '@/components/Question.vue'
import MatchingTS from '@/components/MatchingTS.vue'
import MutationLogsButton from '@/components/MutationLogsButton.vue'
// import PriceDeviation from '@/components/PriceDeviation.vue'
// import ProductName from '@/components/ProductName.vue'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'
import moment from 'moment'

const excludedFields = new Set([
  'Last_Product_Image_URL',
  'Last_Product_Page_URL',
  'Master_Product_External_Link',
  'Master_Product_Image_URL',
  'Master_Product_Image_S3_URL'
])

export default {
  name: 'EnhancedDataTable',
  setup: () => ({ rc: inject('rc') }),
  data () {
    return {
      attributesWidth: undefined,
      grFieldsWidth: undefined,
      groupHeaders: {},
      withintable: '',
      moment,
      sortBy: [],
      page: 1,
      filters: {},
      // questionMenu: {},
      itemsPerPage: 10,
      debounce: null
    }
  },
  components: {
    MatchingTS,
    MutationLogsButton,
    VueJsonPretty,
  },
  props: {
    title: String,
    showExpand: Boolean,
    items: Array,
    headers: Array,
    itemKey: String,
    loading: [Boolean, String],
    selectionStateName: String,
    candidatesLoadingKey: String,
    questionObjectType: String,
    questionNewStatus: String,
    matchedFieldName: String,
    footerProps: Object,
    height: [String, Number],
    groupBy: Array,
    initialAttributesWidth: Number,
    initialItemsPerPage: Number,
    expanded: Array,
    showSelect: Boolean,
    selectStrategy: String,
    headerProps: [Object, Function],
    cellProps: [Object, Function],
    // CRUD action props
    showCrudActions: Boolean,    // Enable CRUD mode (adds toolbar buttons + actions column)
    entityName: String,          // For labels: "Brand", "Product", etc.
    showAddButton: { type: Boolean, default: true },      // Default: true
    showMergeButton: { type: Boolean, default: true },    // Default: true
    showResetFilters: Boolean,   // Show reset filters button
    showUnselectAll: Boolean,    // Show unselect all button
    hideFooter: { type: Boolean, default: false },   // Hide table footer/pagination
    hideTitle: { type: Boolean, default: false },     // Hide table title bar
    // Height mode props
    heightMode: {
      type: String,
      default: 'max',
      validator: (v) => ['auto', 'fixed', 'max'].includes(v)
    },
    maxHeight: {
      type: String,
      default: '80vh'
    }
  },
  emits: ['expanded', 'useAsTemplateProductSk', 'createFromSuggestionsProductMatchingSk', 'add', 'edit', 'delete', 'merge'],
  created() {
    this.attributesWidth = this.initialAttributesWidth || 420
    this.grFieldsWidth = 420
    this.itemsPerPage = this.initialItemsPerPage || 10
  },
  methods: {
    getNestedValue(obj, path) {
      if (!path || !obj) return undefined
      // If path doesn't contain dots, use direct access (faster)
      if (!path.includes('.')) return obj[path]
      // Otherwise traverse the path
      return path.split('.').reduce((current, key) => current?.[key], obj)
    },
    handleRowSelection(event, data) {
      if (event.target.tagName === 'SPAN') {
        // Allows span text to be selected without selecting the row
        return
      }
      data.toggleSelect(data.internalItem)
    },
    handleModel(sks) {
      if (this.selectStrategy === 'page') {
        this.setSelection({ selectionStateName: this.selectionStateName, value: sks })
      }
    },
    uniqueItems(key) {
      const allItems = this.items
      .filter(item => this.getNestedValue(item, key) !== null && this.getNestedValue(item, key) !== undefined)
      .map(item => this.getNestedValue(item, key))
      .sort((a, b) => {
        // Handle different types: convert to string for comparison
        const aStr = String(a)
        const bStr = String(b)
        return aStr.localeCompare(bStr)
      })
      return Array.from(new Set(allItems))
    },
    ...mapMutations([
      'resetCurrentProductMismatchesSks',
      'setCurrentProductSk',
      'setCurrentProductMismatchesSks',
      'setToggledTarget',
      'addToMultiSelection',
      'removeFromMultiSelection',
      'setSelection',
    ]),
    resetFiltersAndSorting() {
      this.filters = {}
      this.withintable = ''
      this.sortBy = []
    },
    clearSelection() {
      this.selected = []
    },
    removeFromSelection(keys) {
      // Remove specific keys from selection
      const keysToRemove = Array.isArray(keys) ? keys : [keys]
      this.selected = this.selected.filter(k => !keysToRemove.includes(k))
    },
    focusSearchInput() {
      setTimeout(() => {
        this.$refs.search?.focus()
      }, 200)
    },
    toggleTarget(targetProductFk) {
      this.setToggledTarget((this.toggledTarget === targetProductFk) ? null : targetProductFk)
    },
    rowProps (data) {
      if (this.selected?.includes(data?.internalItem?.key)) {
        return { class: 'bg-pink-darken-4' }
      }
      return {}
    },
    getProductAttrsLength (item) {
      return Object.keys(JSON.parse(item[this.productAttributesField], (k, v) => (k.toLowerCase() === 'cookie') ? undefined : v)).length
    },
    hasSuggestions (item) {
      return item.Suggested_Brand_Name || item.Suggested_Line_Name || item.Suggested_Product_Name || item.Suggested_Category_Name || item.Suggested_Multi_Pack_Name  || item.Suggested_Benefit_Name || item.Suggested_Discard || item.Suggested_TREX
    },
    debounceSearch (event, type, field) {
      // should I reset the variable first? Doesn't look like it.
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        // this.typing = null
        if (type === 'global') {
          this.withintable = event
        } else if (type === 'filters') {
          this.filters[field] = event
        }
      }, 600)
    },
    customFilter (value, query, item) {
      if (value == null || query == null || typeof value === 'boolean') {
        return -1
      }
      for (const field in item) {
        if (excludedFields.has(field) && item[field] === value) {
          // Don't search within excluded fields
          return -1
        }
      }
      const normalizedValue = this.removeDiacritics(value.toString()).toLocaleLowerCase()
      const normalizedQuery = this.removeDiacritics(query).toLocaleLowerCase()
      return normalizedValue.indexOf(normalizedQuery) !== -1
    },
    removeDiacritics (str) {
      return str.normalize('NFD').replace(/\p{Diacritic}/gu, '')
    },
    formatAsCurrency(price, currency) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(price)
    },
    formatCompactCurrency(price, currency) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, notation: 'compact', maximumFractionDigits: 1 }).format(price)
    },
    parsePayload(jsonString) {
      try {
        return JSON.parse(jsonString);
      } catch {
        return { error: 'Invalid JSON', raw: jsonString };
      }
    },
    computeTooltipImageHeight(sk) {
      return this.tooltipImagesLoaded.includes(sk) ? 'auto' : '700px'
    },
    addVariation(masterProductSk) {
      // Adds a new variation, based on masterProductSk
      const baseMasterProduct = this.filteredItems?.find(x => x.Master_Product_SK === masterProductSk)
      if (baseMasterProduct) {
        this.$emit('addVariation', baseMasterProduct)
      } else {
        console.error('Master Product not found on which to base a variation. masterProductSk:', masterProductSk)
      }
    },
    openAllGroupsOnPage() {
      this.$nextTick(() => {
        Object.values(this.groupHeaders)?.forEach(header => {
          if (!header.isGroupOpen(header.item)) {
            header.toggleGroup(header.item)
          }
        })
      })
    }
  },
  computed: {
    ...mapState(['currentProductMismatchesSks', 'currentMasterProductSk', 'toggledTarget', 'fixedHeader']),
    selected: {
      get () {
        if (this.selectStrategy === 'single') {
          if (this.$store.state[this.selectionStateName]) {
            // v-data-table v-model expects an array, so wrap state value in array
            return [this.$store.state[this.selectionStateName]]
          } else {
            return []
          }
        }
        if (this.selectStrategy === 'page') {
          if (this.$store.state[this.selectionStateName]?.length) {
            return this.$store.state[this.selectionStateName]
          } else {
            return []
          }
        }
        return []
      },
      set (value) {
        if (this.selectStrategy === 'page') {
          this.setSelection({ selectionStateName: this.selectionStateName, value: value })
          if (this.selectionStateName === 'currentProductMismatchesSks') {
            this.setCurrentProductMismatchesSks(this.currentProductMismatchesSks)
          }
        } else if (this.selectStrategy === 'single') {
          // Since v-data-table v-model is an array, save the first (and only) value
          this.setSelection({ selectionStateName: this.selectionStateName, value: value?.at(0) || null })
          // Ensures currentMasterProductSk is synced with Catalog window:
          if (this.selectionStateName === 'currentProductSk') {
            this.setCurrentProductSk(value?.at(0))
          }
        }
      }
    },
    filteredItems () {
      let filteredItems = this.items
      filteredItems = Object.entries(this.filters).reduce((acc, [headerKey, headerValue]) => {
        if (Array.isArray(headerValue)) {
          // v-select or v-autocomplete
          if (headerValue.length) {
            // Only filter if array has values
            acc = acc.filter(item => {
              const itemValue = this.getNestedValue(item, headerKey)
              // Handle boolean values
              if (typeof itemValue === 'boolean') {
                return headerValue.some(val => itemValue === val)
              }
              const itemValueStr = itemValue?.toString()?.toLowerCase()
              if (headerKey === 'Master_Segment_Name') {
                return headerValue.some(val => {
                  const valStr = typeof val === 'boolean' ? val : val.toLowerCase()
                  return itemValueStr == valStr
                })
              } else {
                return headerValue.some(val => {
                  const valStr = typeof val === 'boolean' ? val.toString().toLowerCase() : val.toLowerCase()
                  return itemValueStr?.includes(valStr)
                })
              }
            })
          }
          // If array is empty, don't filter (return all items)
        } else if (headerValue) {
          // Single value filter (text field or number field)
          const headerDef = this.headers?.find(h => h.key === headerKey)
          acc = acc.filter(item => {
            const itemValue = this.getNestedValue(item, headerKey)
            // Handle boolean values
            if (typeof itemValue === 'boolean') {
              return itemValue === headerValue
            }
            // Handle number filter type - exact match or starts with
            if (headerDef?.filterType === 'number') {
              const itemStr = itemValue?.toString() || ''
              const searchStr = headerValue.toString()
              return itemStr.startsWith(searchStr)
            }
            const itemValueStr = this.removeDiacritics(itemValue?.toString() || '').toLowerCase()
            const searchValue = this.removeDiacritics(headerValue.toString()).toLowerCase()
            return itemValueStr?.includes(searchValue)
          })
        }
        return acc
      }, filteredItems)
      filteredItems = filteredItems?.filter(item => this.headers.filter(h => h.filterSelectionStateName)
          .every(h => !this.$store.state[h.filterSelectionStateName] || item[h.key] === this.$store.state[h.filterSelectionStateName]))
      return filteredItems
    },
    defaultHeight () {
      // Only set fixed height when the current page has enough items to cause scrolling
      // With pagination, we only show itemsPerPage items at a time
      // Apply fixed height when showing more than ~10 rows (which is when scrolling typically becomes useful)
      if (!this.fixedHeader) return undefined
      if (this.heightMode === 'auto') return undefined
      if (this.heightMode === 'max') return undefined  // CSS handles max-height

      // 'fixed' mode - original behavior
      if (this.itemsPerPage === -1) {
        // Show all mode: use total item count
        return (this.filteredItems?.length > 10) ? (this.maxHeight || '65vh') : undefined
      }
      // Paginated mode: only fix headers if the page size itself is large enough to need scrolling
      return this.itemsPerPage > 10 ? (this.maxHeight || '65vh') : undefined
    },
    tableContainerClass() {
      if (this.fixedHeader && this.heightMode === 'max') {
        return 'max-height-mode'
      }
      return ''
    },
    productAttributesField () {
      // return this.title === 'Products' ? 'Product_Attributes_JSON_Orig' : 'Product_Attributes_JSON_PostProc'
      return 'EXT_Product_Info_JSON'
    },
    allMasterProductsLoading() {
      return this.rc?.$apollo.queries.allMasterProducts.loading
    },
    // Returns full item records for selected items (used for merge)
    selectedRecords() {
      if (!this.selected?.length || !this.items?.length) return []
      return this.items.filter(item => this.selected.includes(item[this.itemKey]))
    },
  },
  watch: {
    page (newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.title === 'Master Products') {
          this.openAllGroupsOnPage()
        }
      }
    },
    candidatesLoadingKey (newVal, oldVal) {
      // when candidates stop loading
      if (oldVal && !newVal) {
        this.sortBy = [{ key: 'CandidateScore', order: 'desc' }]
        this.page = 1
      }
    }
  }
}
</script>
<style scoped>
span:hover {
  cursor: text !important;
}
.underline-on-hover:hover {
  text-decoration: underline !important;
}
.fixed-filters {
  top: 37px !important;
  position: sticky !important;
  background-color: #323638 !important;
  z-index: 1;
}
/* Scrollbar track (background) */
.scrollbar::-webkit-scrollbar-track {
  background: #333; /* Dark grey track background */
}
/* Scrollbar handle */
.scrollbar::-webkit-scrollbar-thumb {
  background: #555; /* Medium grey handle */
}
/* Handle hover */
.scrollbar::-webkit-scrollbar-thumb:hover {
  background: #222; /* Very dark grey handle on hover */
}
/* Scrollbar width & height */
.scrollbar::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}
.custom-chip-hover:hover {
  opacity: 0.8;
}
/* Max-height mode: allows table to shrink to content while respecting max-height */
.max-height-mode :deep(.v-table__wrapper) {
  max-height: v-bind('maxHeight || "65vh"');
  overflow-y: auto;
}
.max-height-mode :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}
.payload-json-container {
  max-width: 400px;
  max-height: 200px;
  overflow: auto;
}
.payload-json-container :deep(.vjs-tree) {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
}
</style>
